import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import API from '../services/api';
import { getViolationColor } from '../types/caseForm';
import { PlusIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, ArrowDownTrayIcon, ArrowUpTrayIcon, FunnelIcon, XMarkIcon, CheckBadgeIcon, GlobeAltIcon, ExclamationTriangleIcon, ShieldExclamationIcon, MapPinIcon,StarIcon, UserIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import UserAvatar from '../components/UserAvatar';
import ExportModal, { ExportFilters } from '../components/ExportModal';
import ImportModal from '../components/ImportModal';
import useCountries from '../hooks/useCountries';

interface Case {
  id: string;
  caseNumber: string;
  incidentDate: string;
  reportedDate: string;
  country: string;
  region: string;
  indigenousGroup: string;
  violationTypes: string[];
  otherViolationTypes: string[];
  status: string;
  priority: number;
  isPrimaryData: boolean;
  documentorName?: string;
  submittedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  assignedTo?: {
    id: string;
    firstName: string;
    lastName: string;
    role: string;
    profilePicture?: string | null;
  };
}

const FilterChip: React.FC<{ label: string; onClear: () => void }> = ({ label, onClear }) => (
  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
    {label}
    <button
      type="button"
      onClick={onClear}
      className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-emerald-200"
      aria-label={`Remove ${label} filter`}
    >
      <XMarkIcon className="h-3 w-3" />
    </button>
  </span>
);

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const CASE_FILTERS_STORAGE_KEY = 'caseListFilters';

const getSavedFilters = (): Record<string, any> => {
  try {
    const saved = sessionStorage.getItem(CASE_FILTERS_STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
};

const Cases: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const { countries } = useCountries();
  const isDocumenter = user?.role === 'DOCUMENTER';
  const isResearcher = user?.role === 'RESEARCHER';
  const [saved] = useState(getSavedFilters);
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(saved.searchTerm ?? '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(saved.searchTerm ?? '');
  const [filterStatus, setFilterStatus] = useState<string[]>(Array.isArray(saved.filterStatus) ? saved.filterStatus : []);
  const [filterPriority, setFilterPriority] = useState(saved.filterPriority ?? 'all');
  const [filterAssignment, setFilterAssignment] = useState(saved.filterAssignment ?? 'all');
  const [filterCountries, setFilterCountries] = useState<string[]>(saved.filterCountries ?? (user?.role === 'ADMIN' ? [] : (user?.countries || [])));
  const [countryInput, setCountryInput] = useState('');
  const [filterYear, setFilterYear] = useState(saved.filterYear ?? String(new Date().getFullYear()));
  const [filterViolationTypes, setFilterViolationTypes] = useState<string[]>(saved.filterViolationTypes ?? []);
  const [filterOtherViolationTypes, setFilterOtherViolationTypes] = useState<string[]>(saved.filterOtherViolationTypes ?? []);
  const [filterIpriRegion, setFilterIpriRegion] = useState<string[]>(saved.filterIpriRegion ?? []);
  const [filterDataSource, setFilterDataSource] = useState<'all' | 'primary' | 'secondary'>(saved.filterDataSource ?? 'all');
  const [filterIsLDSFApplicant, setFilterIsLDSFApplicant] = useState(saved.filterIsLDSFApplicant ?? 'all');
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false); // confirmed duplicates are archived; hidden by default
  const [ipriRegionDropdownOpen, setIpriRegionDropdownOpen] = useState(false);
  const [violationTypeDropdownOpen, setViolationTypeDropdownOpen] = useState(false);
  const [otherViolationTypeDropdownOpen, setOtherViolationTypeDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(saved.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(saved.pageSize ?? 10);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [selectedCases, setSelectedCases] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [viewMode, setViewMode] = useState<'all' | 'my'>(saved.viewMode ?? (isDocumenter || isResearcher ? 'my' : 'all'));

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Persist filters to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(CASE_FILTERS_STORAGE_KEY, JSON.stringify({
      searchTerm, filterStatus, filterPriority, filterAssignment,
      filterCountries, filterIpriRegion, filterYear, filterViolationTypes,
      filterOtherViolationTypes, filterIsLDSFApplicant, filterDataSource,
      currentPage, pageSize, viewMode,
    }));
  }, [searchTerm, filterStatus, filterPriority, filterAssignment, filterCountries, filterIpriRegion, filterYear, filterViolationTypes, filterOtherViolationTypes, filterIsLDSFApplicant, filterDataSource, currentPage, pageSize, viewMode]);

  useEffect(() => {
    fetchCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearchTerm, filterStatus, filterPriority, filterAssignment, filterCountries, filterIpriRegion, filterYear, filterViolationTypes, filterOtherViolationTypes, filterIsLDSFApplicant, filterDataSource, pageSize, viewMode, showArchived]);

  useEffect(() => {
    // Only fetch users if user can assign cases
    if (user && ['ADMIN', 'APPROVER'].includes(user.role)) {
      fetchAvailableUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchCases = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(filterStatus.length === 1 ? { status: filterStatus[0] } : {}),
        ...(filterStatus.length === 0 && !showArchived ? { excludeStatus: 'ARCHIVED' } : {}),
        ...(filterPriority !== 'all' && { priority: filterPriority }),
        ...(filterAssignment !== 'all' && { assignedTo: filterAssignment }),
        ...(filterYear && { year: filterYear }),
        ...(filterIsLDSFApplicant !== 'all' && { isLDSFApplicant: filterIsLDSFApplicant }),
        ...(filterDataSource !== 'all' && { isPrimaryData: filterDataSource === 'primary' ? 'true' : 'false' }),
        ...(viewMode === 'my' && { submittedBy: 'me' }),
        ...(viewMode === 'all' && isAdmin && { includeDrafts: 'true' }),
      });
      if (filterStatus.length > 1) filterStatus.forEach(s => params.append('status', s));
      filterCountries.forEach(c => params.append('country', c));
      filterIpriRegion.forEach(r => params.append('ipriRegion', r));
      filterViolationTypes.forEach(v => params.append('violationType', v));
      filterOtherViolationTypes.forEach(v => params.append('otherViolationType', v));

      const response = await API.get(`/api/cases?${params}`);
      setCases(response.data.cases || []);
      setPagination(response.data.pagination || pagination);
    } catch (error) {
      console.error('Failed to fetch cases:', error);
      setCases([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  // const handleStatusFilter = (status: string) => {
  //   setFilterStatus(status);
  //   setCurrentPage(1); // Reset to first page when filtering
  // };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Bulk selection functions
  const isAllSelected = cases.length > 0 && cases.every(case_ => selectedCases.has(case_.id));
  const isIndeterminate = cases.some(case_ => selectedCases.has(case_.id)) && !isAllSelected;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedCases(new Set());
    } else {
      setSelectedCases(new Set(cases.map(case_ => case_.id)));
    }
  };

  const handleSelectCase = (caseId: string) => {
    const newSelected = new Set(selectedCases);
    if (newSelected.has(caseId)) {
      newSelected.delete(caseId);
    } else {
      newSelected.add(caseId);
    }
    setSelectedCases(newSelected);
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await API.get('/api/users');
      const users = response.data.users || [];
      // Filter to only show users who can be assigned cases
      const assignableUsers = users.filter((u: any) =>
        ['RESEARCHER', 'APPROVER', 'ADMIN'].includes(u.role)
      );
      setAvailableUsers(assignableUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleBulkStatusUpdate = async (newStatus: string) => {
    if (selectedCases.size === 0) return;

    setBulkActionLoading(true);
    try {
      const caseIds = Array.from(selectedCases);
      await API.put('/api/cases/bulk/status', {
        caseIds,
        status: newStatus
      });

      // Refresh the cases list
      await fetchCases();
      setSelectedCases(new Set());
    } catch (error: any) {
      showToast('error', error.response?.data?.message || 'Failed to update case status');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkPriorityUpdate = async (newPriority: number) => {
    if (selectedCases.size === 0) return;

    setBulkActionLoading(true);
    try {
      const caseIds = Array.from(selectedCases);
      await API.put('/api/cases/bulk/priority', {
        caseIds,
        priority: newPriority
      });

      // Refresh the cases list
      await fetchCases();
      setSelectedCases(new Set());
    } catch (error: any) {
      showToast('error', error.response?.data?.message || 'Failed to update case priority');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkAssignment = async (assignedToId: string | null) => {
    if (selectedCases.size === 0) return;

    const assignee = assignedToId ? availableUsers.find(u => u.id === assignedToId) : null;
    const actionText = assignee
      ? `assign ${selectedCases.size} case(s) to ${assignee.firstName} ${assignee.lastName}`
      : `unassign ${selectedCases.size} case(s)`;

    const confirmed = await confirm({
      title: 'Bulk Assignment',
      message: `Are you sure you want to ${actionText}?`,
      confirmLabel: 'Confirm'
    });
    if (!confirmed) return;

    setBulkActionLoading(true);
    try {
      const caseIds = Array.from(selectedCases);
      const response = await API.put('/api/cases/bulk/assign', {
        caseIds,
        assignedToId
      });

      showToast('success', response.data.message);

      // Refresh the cases list
      await fetchCases();
      setSelectedCases(new Set());
    } catch (error: any) {
      showToast('error', error.response?.data?.message || 'Failed to assign cases');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Clear selections when page changes
  useEffect(() => {
    setSelectedCases(new Set());
  }, [currentPage]);

  // Export handler
  const handleExport = async (filters: ExportFilters, format: 'csv' | 'excel') => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams();

      // Add filters to params
      if (filters.countries && filters.countries.length > 0) {
        filters.countries.forEach(c => params.append('countries', c));
      }
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.violationTypes && filters.violationTypes.length > 0) {
        filters.violationTypes.forEach(v => params.append('violationTypes', v));
      }
      if (filters.perpetratorTypes && filters.perpetratorTypes.length > 0) {
        filters.perpetratorTypes.forEach(p => params.append('perpetratorTypes', p));
      }
      if (filters.indigenousGroups && filters.indigenousGroups.length > 0) {
        filters.indigenousGroups.forEach(g => params.append('indigenousGroups', g));
      }
      if (filters.includeVictims) params.append('includeVictims', 'true');
      if (filters.includePerpetrators) params.append('includePerpetrators', 'true');
      if (filters.includeDocuments) params.append('includeDocuments', 'true');

      const endpoint = format === 'csv' ? '/api/export/cases/csv' : '/api/export/cases/excel';
      const response = await API.get(`${endpoint}?${params.toString()}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ipri-cases.${format === 'csv' ? 'csv' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Export failed:', error);
      showToast('error', error.response?.data?.message || 'Failed to export cases');
    } finally {
      setExportLoading(false);
    }
  };

  // Check if current user can assign cases
  const isAdmin = user?.role === 'ADMIN';
  const canAssignCases = user && ['ADMIN', 'APPROVER'].includes(user.role);

  const activeFilterCount = [
    filterStatus.length > 0,
    filterPriority !== 'all',
    filterAssignment !== 'all',
    filterCountries.length > 0,
    filterIpriRegion.length > 0,
    filterViolationTypes.length > 0,
    filterOtherViolationTypes.length > 0,
    filterIsLDSFApplicant !== 'all',
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setFilterStatus([]);
    setFilterPriority('all');
    setFilterAssignment('all');
    setFilterCountries([]);
    setFilterIpriRegion([]);
    setFilterViolationTypes([]);
    setFilterOtherViolationTypes([]);
    setFilterIsLDSFApplicant('all');
    setCurrentPage(1);
    sessionStorage.removeItem(CASE_FILTERS_STORAGE_KEY);
  };

  const toggleStatus = (value: string) => {
    setFilterStatus(prev =>
      prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]
    );
    setCurrentPage(1);
  };

  const toggleIpriRegion = (value: string) => {
    setFilterIpriRegion(prev =>
      prev.includes(value) ? prev.filter(r => r !== value) : [...prev, value]
    );
    setCurrentPage(1);
  };

  const toggleViolationType = (value: string) => {
    setFilterViolationTypes(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
    setCurrentPage(1);
  };

  const toggleOtherViolationType = (value: string) => {
    setFilterOtherViolationTypes(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
    setCurrentPage(1);
  };

  // const getPriorityColor = (priority: number) => {
  //   if (priority >= 5) return 'text-red-600';
  //   if (priority >= 3) return 'text-yellow-600';
  //   return 'text-green-600';
  // };

  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 1: return 'Low';
      case 2: return 'Med-low';
      case 3: return 'Medium';
      case 4: return 'Med-high';
      case 5: return 'High';
      default: return 'Unknown';
    }
  };

  const getViolationTypeColor = (type: string) => getViolationColor(type).soft;

  const getViolationTypeLabel = (type: string) => {
    return type.split('_').map(word =>
      word.charAt(0) + word.slice(1).toLowerCase()
    ).join(' ');
  };

  return (
    <>
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
      />

      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={fetchCases}
      />

      {showFiltersModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center">
          <div className="relative top-20 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Filters</h3>
              <button
                type="button"
                onClick={() => setShowFiltersModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label="Close filters"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* 1. Status */}
              <div className="relative">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><CheckBadgeIcon className="h-4 w-4" />Status</label>
                <button
                  type="button"
                  onClick={() => setStatusDropdownOpen(prev => !prev)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-left text-sm focus:outline-none focus:ring-primary focus:border-primary flex items-center justify-between"
                >
                  <span className={filterStatus.length === 0 ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}>
                    {filterStatus.length === 0 ? 'All Status' : `${filterStatus.length} selected`}
                  </span>
                  <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {statusDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg py-1">
                    {[
                      ...((isAdmin || viewMode === 'my') ? [{ value: 'DRAFT', label: 'Draft' }] : []),
                      { value: 'FOR_VALIDATION', label: 'For Validation' },
                      { value: 'IN_RESEARCH', label: 'In Research' },
                      { value: 'APPROVED', label: 'Approved' },
                      { value: 'FOR_APPROVAL', label: 'For Approval' },
                      { value: 'REJECTED', label: 'Rejected' },
                    ].map(({ value, label }) => (
                      <label
                        key={value}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filterStatus.includes(value)}
                          onChange={() => toggleStatus(value)}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Show archived duplicates toggle */}
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => { setShowArchived(e.target.checked); setCurrentPage(1); }}
                    className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                  />
                  Show archived duplicates
                </label>
              </div>

              {/* 2. IPRI Region */}
              <div className="relative">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><GlobeAltIcon className="h-4 w-4" />IPRI Region</label>
                <button
                  type="button"
                  onClick={() => setIpriRegionDropdownOpen(prev => !prev)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-left text-sm focus:outline-none focus:ring-primary focus:border-primary flex items-center justify-between"
                >
                  <span className={filterIpriRegion.length === 0 ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}>
                    {filterIpriRegion.length === 0 ? 'All Regions' : `${filterIpriRegion.length} selected`}
                  </span>
                  <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${ipriRegionDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {ipriRegionDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg py-1">
                    {[
                      { value: 'LAC', label: 'LAC (Latin America & Caribbean)' },
                      { value: 'ASIA', label: 'Asia' },
                      { value: 'AFRICA', label: 'Africa' },
                    ].map(({ value, label }) => (
                      <label
                        key={value}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filterIpriRegion.includes(value)}
                          onChange={() => toggleIpriRegion(value)}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* 3. Violation Type */}
              <div className="relative">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><ExclamationTriangleIcon className="h-4 w-4" />Violation Type</label>
                <button
                  type="button"
                  onClick={() => setViolationTypeDropdownOpen(prev => !prev)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-left text-sm focus:outline-none focus:ring-primary focus:border-primary flex items-center justify-between"
                >
                  <span className={filterViolationTypes.length === 0 ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}>
                    {filterViolationTypes.length === 0 ? 'All Violation Types' : `${filterViolationTypes.length} selected`}
                  </span>
                  <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${violationTypeDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {violationTypeDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg py-1">
                    {[
                      { value: 'KILLING', label: 'Killing' },
                      { value: 'CRIMINALIZATION', label: 'Criminalization' },
                      { value: 'OTHER', label: 'Other' },
                    ].map(({ value, label }) => (
                      <label
                        key={value}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filterViolationTypes.includes(value)}
                          onChange={() => toggleViolationType(value)}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* 4. Other Violation Type */}
              <div className="relative">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><ShieldExclamationIcon className="h-4 w-4" />Other Violation Type</label>
                <button
                  type="button"
                  onClick={() => setOtherViolationTypeDropdownOpen(prev => !prev)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-left text-sm focus:outline-none focus:ring-primary focus:border-primary flex items-center justify-between"
                >
                  <span className={filterOtherViolationTypes.length === 0 ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}>
                    {filterOtherViolationTypes.length === 0 ? 'All Other Violation Types' : `${filterOtherViolationTypes.length} selected`}
                  </span>
                  <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${otherViolationTypeDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {otherViolationTypeDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg py-1 max-h-48 overflow-auto">
                    {[
                      { value: 'FPIC_VIOLATION', label: 'FPIC Violation' },
                      { value: 'ARBITRARY_DETENTION', label: 'Arbitrary Detention' },
                      { value: 'KIDNAPPING', label: 'Kidnapping' },
                      { value: 'PHYSICAL_INJURY', label: 'Physical Injury' },
                      { value: 'ENFORCED_DISAPPEARANCE', label: 'Enforced Disappearance' },
                      { value: 'SEXUAL_VIOLENCE', label: 'Sexual Violence' },
                      { value: 'ONLINE_ATTACKS', label: 'Online Attacks' },
                      { value: 'FORCED_DISPLACEMENT', label: 'Forced Displacement' },
                      { value: 'LAND_GRABBING', label: 'Land Grabbing' },
                      { value: 'ENVIRONMENTAL_DESTRUCTION', label: 'Environmental Destruction' },
                      { value: 'CULTURAL_DESTRUCTION', label: 'Cultural Destruction' },
                      { value: 'THREATS_AND_INTIMIDATION', label: 'Threats And Intimidation' },
                      { value: 'VILIFICATION', label: 'Vilification' },
                      { value: 'LOSS_OF_LIVELIHOODS', label: 'Loss of Livelihoods' },
                      { value: 'OTHER', label: 'Other' },
                    ].map(({ value, label }) => (
                      <label
                        key={value}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filterOtherViolationTypes.includes(value)}
                          onChange={() => toggleOtherViolationType(value)}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* 5. Countries */}
              <div className="sm:col-span-2">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><MapPinIcon className="h-4 w-4" />Countries</label>
                <div className="relative">
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="Search and select countries..."
                    value={countryInput}
                    onChange={(e) => setCountryInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const val = countryInput.trim();
                        if (val && !filterCountries.includes(val)) {
                          setFilterCountries(prev => [...prev, val]);
                          setCurrentPage(1);
                        }
                        setCountryInput('');
                      }
                    }}
                  />
                  {countryInput.trim() && (
                    <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-auto">
                      {countries
                        .filter(c => c.toLowerCase().includes(countryInput.toLowerCase()) && !filterCountries.includes(c))
                        .slice(0, 10)
                        .map(c => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => {
                              setFilterCountries(prev => [...prev, c]);
                              setCountryInput('');
                              setCurrentPage(1);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                          >
                            {c}
                          </button>
                        ))}
                      {countries.filter(c => c.toLowerCase().includes(countryInput.toLowerCase()) && !filterCountries.includes(c)).length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No matching countries</div>
                      )}
                    </div>
                  )}
                </div>
                {filterCountries.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {filterCountries.map(c => (
                      <span key={c} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                        {c}
                        <button
                          type="button"
                          onClick={() => { setFilterCountries(prev => prev.filter(x => x !== c)); setCurrentPage(1); }}
                          className="ml-1 text-emerald-600 hover:text-emerald-800"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* 6. Priority */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><StarIcon className="h-4 w-4" />Priority</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                  value={filterPriority}
                  onChange={(e) => { setFilterPriority(e.target.value); setCurrentPage(1); }}
                >
                  <option value="all">All Priority</option>
                  <option value="1">Low</option>
                  <option value="2">Med-low</option>
                  <option value="3">Medium</option>
                  <option value="4">Med-high</option>
                  <option value="5">High</option>
                </select>
              </div>

              {/* 7. Assignment */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><UserIcon className="h-4 w-4" />Assignment</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                  value={filterAssignment}
                  onChange={(e) => { setFilterAssignment(e.target.value); setCurrentPage(1); }}
                >
                  <option value="all">All Assignment</option>
                  <option value="me">Assigned to me</option>
                  <option value="unassigned">Unassigned</option>
                  <option value="assigned">Assigned</option>
                  {availableUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.firstName} {u.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* 8. LDSF Applicant */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"><DocumentTextIcon className="h-4 w-4" />LDSF Applicant</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                  value={filterIsLDSFApplicant}
                  onChange={(e) => { setFilterIsLDSFApplicant(e.target.value); setCurrentPage(1); }}
                >
                  <option value="all">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-between">
              <button
                type="button"
                onClick={clearAllFilters}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
              >
                Clear all
              </button>
              <button
                type="button"
                onClick={() => setShowFiltersModal(false)}
                className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 text-sm font-medium"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <div>
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 font-heading">Cases</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Manage and review human rights violation cases
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {canAssignCases && (
              <button
                onClick={() => setShowImportModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                title="Bulk-import cases from CSV"
              >
                <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                Import
              </button>
            )}
            <button
              onClick={() => setShowExportModal(true)}
              disabled={exportLoading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export cases to CSV or Excel"
            >
              <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
              {exportLoading ? 'Exporting...' : 'Export'}
            </button>
            <Link
              to="/cases/new"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              New Case
            </Link>
          </div>
        </div>

      {/* View Mode Tabs */}
      {!isDocumenter && (
        <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8" aria-label="View mode">
            <button
              onClick={() => { setViewMode('all'); setCurrentPage(1); if (!isAdmin) setFilterStatus(prev => prev.filter(s => s !== 'DRAFT')); }}
              className={`whitespace-nowrap pb-3 px-1 border-b-2 text-sm font-medium ${
                viewMode === 'all'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              All Cases
            </button>
            <button
              onClick={() => { setViewMode('my'); setCurrentPage(1); }}
              className={`whitespace-nowrap pb-3 px-1 border-b-2 text-sm font-medium ${
                viewMode === 'my'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              My Submissions
            </button>
          </nav>
        </div>
      )}

      {/* Search and Filter */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="inline-flex rounded-md shadow-sm">
          {([
            { value: 'all', label: 'All', badge: null },
            { value: 'primary', label: 'Primary', badge: 'P' },
            { value: 'secondary', label: 'Secondary', badge: 'S' },
          ] as const).map(({ value, label, badge }, i) => (
            <button
              key={value}
              onClick={() => { setFilterDataSource(value); setCurrentPage(1); }}
              className={`px-3 py-2 text-sm font-medium border flex items-center gap-1.5 ${
                filterDataSource === value
                  ? 'bg-emerald-600 text-white border-emerald-600 z-10'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              } ${i === 0 ? 'rounded-l-md' : ''} ${i === 2 ? 'rounded-r-md' : ''} ${i > 0 ? '-ml-px' : ''}`}
            >
              {badge && (
                <span className={`inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold rounded ${
                  filterDataSource === value
                    ? 'bg-white text-emerald-700 ring-1 ring-white/50'
                    : value === 'primary'
                    ? 'bg-white text-green-700 ring-1 ring-green-300'
                    : 'bg-white text-yellow-700 ring-1 ring-yellow-300'
                }`}>{badge}</span>
              )}
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search cases..."
              className="pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-full focus:outline-none focus:ring-primary focus:border-primary"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
        </div>

        <select
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
          value={filterYear}
          onChange={(e) => { setFilterYear(e.target.value); setCurrentPage(1); }}
          aria-label="Filter by reported year"
        >
          <option value="">All years</option>
          {Array.from({ length: new Date().getFullYear() - 1999 }, (_, i) => new Date().getFullYear() - i).map((y) => (
            <option key={y} value={String(y)}>{y}</option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => setShowFiltersModal(true)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 relative"
        >
          <FunnelIcon className="h-5 w-5 mr-2" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-600 text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Active filter chips */}
      {activeFilterCount > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {filterStatus.map(s => (
            <FilterChip key={s} label={`Status: ${s.replace(/_/g, ' ')}`} onClear={() => { toggleStatus(s); }} />
          ))}
          {filterPriority !== 'all' && (
            <FilterChip label={`Priority: ${getPriorityLabel(parseInt(filterPriority))}`} onClear={() => { setFilterPriority('all'); setCurrentPage(1); }} />
          )}
          {filterAssignment !== 'all' && (
            <FilterChip label={`Assignment: ${filterAssignment}`} onClear={() => { setFilterAssignment('all'); setCurrentPage(1); }} />
          )}
          {filterCountries.map(c => (
            <FilterChip key={`chip-c-${c}`} label={`Country: ${c}`} onClear={() => { setFilterCountries(prev => prev.filter(x => x !== c)); setCurrentPage(1); }} />
          ))}
          {filterIpriRegion.map(r => (
            <FilterChip key={`chip-r-${r}`} label={`Region: ${r}`} onClear={() => toggleIpriRegion(r)} />
          ))}
          {filterViolationTypes.map(v => (
            <FilterChip
              key={`chip-v-${v}`}
              label={`Violation: ${getViolationTypeLabel(v)}`}
              onClear={() => toggleViolationType(v)}
            />
          ))}
          {filterOtherViolationTypes.map(v => (
            <FilterChip
              key={`chip-ov-${v}`}
              label={`Other Violation: ${getViolationTypeLabel(v)}`}
              onClear={() => toggleOtherViolationType(v)}
            />
          ))}
          {filterIsLDSFApplicant !== 'all' && (
            <FilterChip label={`LDSF Applicant: ${filterIsLDSFApplicant === 'true' ? 'Yes' : 'No'}`} onClear={() => { setFilterIsLDSFApplicant('all'); setCurrentPage(1); }} />
          )}
        </div>
      )}

      {/* Bulk Actions Toolbar */}
      {selectedCases.size > 0 && (
        <div className="mb-4 bg-secondary/10 border border-secondary/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-sm font-medium text-blue-900">
                {selectedCases.size} case{selectedCases.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => setSelectedCases(new Set())}
                className="ml-4 text-sm text-blue-600 hover:text-blue-800"
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center space-x-4">
              {/* Bulk Status Update */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-blue-900">Change status to:</span>
                <select
                  onChange={async (e) => {
                    const value = e.target.value;
                    e.target.value = '';
                    if (!value) return;
                    const confirmed = await confirm({
                      title: 'Bulk Status Update',
                      message: `Are you sure you want to change ${selectedCases.size} case(s) to ${value.replace('_', ' ')}?`,
                      confirmLabel: 'Confirm'
                    });
                    if (confirmed) handleBulkStatusUpdate(value);
                  }}
                  className="px-3 py-1 border border-blue-300 rounded text-sm bg-white"
                  disabled={bulkActionLoading}
                >
                  <option value="">Select status...</option>
                  <option value="FOR_VALIDATION">For Validation</option>
                  <option value="IN_RESEARCH">In Research</option>
                  <option value="APPROVED">Approved</option>
                  <option value="FOR_APPROVAL">For Approval</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>

              {/* Bulk Priority Update */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-blue-900">Change priority to:</span>
                <select
                  onChange={async (e) => {
                    const value = e.target.value;
                    e.target.value = '';
                    if (!value) return;
                    const confirmed = await confirm({
                      title: 'Bulk Priority Update',
                      message: `Are you sure you want to change ${selectedCases.size} case(s) to priority ${getPriorityLabel(parseInt(value))}?`,
                      confirmLabel: 'Confirm'
                    });
                    if (confirmed) handleBulkPriorityUpdate(parseInt(value));
                  }}
                  className="px-3 py-1 border border-blue-300 rounded text-sm bg-white"
                  disabled={bulkActionLoading}
                >
                  <option value="">Select priority...</option>
                  <option value="1">Low</option>
                  <option value="2">Med-low</option>
                  <option value="3">Medium</option>
                  <option value="4">Med-high</option>
                  <option value="5">High</option>
                </select>
              </div>

              {/* Bulk Assignment - Only for Admins/Managers */}
              {canAssignCases && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-blue-900">Assign to:</span>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleBulkAssignment(e.target.value);
                      } else {
                        handleBulkAssignment(null); // Unassign
                      }
                      e.target.value = ''; // Reset select
                    }}
                    className="px-3 py-1 border border-blue-300 rounded text-sm bg-white"
                    disabled={bulkActionLoading}
                  >
                    <option value="">Select assignee...</option>
                    <option value="">⚫ Unassign All</option>
                    {availableUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.role})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {bulkActionLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cases Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = isIndeterminate;
                  }}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-emerald-600 shadow-sm focus:border-emerald-300 focus:ring focus:ring-emerald-200 focus:ring-opacity-50"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Case
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Violation Types
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                IP Group
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Reported Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Priority
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Documenter
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Assigned To
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={12} className="px-6 py-4 text-center">
                  <div className="inline-flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600 mr-2"></div>
                    Loading cases...
                  </div>
                </td>
              </tr>
            ) : cases.length === 0 ? (
              <tr>
                <td colSpan={12} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                  No cases found
                </td>
              </tr>
            ) : (
              cases.map((case_) => (
                <tr key={case_.id} className={selectedCases.has(case_.id) ? 'bg-blue-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedCases.has(case_.id)}
                      onChange={() => handleSelectCase(case_.id)}
                      className="rounded border-gray-300 text-emerald-600 shadow-sm focus:border-emerald-300 focus:ring focus:ring-emerald-200 focus:ring-opacity-50"
                    />
                  </td>
                  <td className="px-6 py-4 text-sm font-medium">
                    <div className="flex items-center gap-1.5">
                      <span className={`inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold rounded ${case_.isPrimaryData ? 'bg-white text-green-700 ring-1 ring-green-300' : 'bg-white text-yellow-700 ring-1 ring-yellow-300'}`} title={case_.isPrimaryData ? 'Primary data' : 'Secondary data'}>
                        {case_.isPrimaryData ? 'P' : 'S'}
                      </span>
                      <Link to={`/cases/${case_.id}`} className={case_.isPrimaryData ? 'text-green-600 hover:text-green-900' : 'text-yellow-600 hover:text-yellow-900'}>
                        {case_.caseNumber}
                      </Link>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 pl-5.5">{case_.country || '—'}</p>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {(() => {
                      const allTypes = [
                        ...case_.violationTypes.map(t => ({ key: `v-${t}`, type: t })),
                        ...(case_.otherViolationTypes || []).map(t => ({ key: `ov-${t}`, type: t })),
                      ];
                      const visible = allTypes.slice(0, 2);
                      const remaining = allTypes.slice(2);
                      return (
                        <div className="flex items-center gap-1">
                          {visible.map(({ key, type }) => (
                            <span
                              key={key}
                              className={`px-2 py-1 text-xs rounded-lg ${getViolationTypeColor(type)}`}
                            >
                              {getViolationTypeLabel(type)}
                            </span>
                          ))}
                          {remaining.length > 0 && (
                            <span className="relative group">
                              <span className="px-2 py-1 text-xs rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600">
                                +{remaining.length}
                              </span>
                              <div className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:flex flex-wrap gap-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 min-w-max">
                                {remaining.map(({ key, type }) => (
                                  <span
                                    key={key}
                                    className={`px-2 py-1 text-xs rounded-lg ${getViolationTypeColor(type)}`}
                                  >
                                    {getViolationTypeLabel(type)}
                                  </span>
                                ))}
                              </div>
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <span title={case_.indigenousGroup}>
                      {case_.indigenousGroup && case_.indigenousGroup.length > 20
                        ? `${case_.indigenousGroup.slice(0, 20)}…`
                        : case_.indigenousGroup || '—'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {case_.reportedDate ? new Date(case_.reportedDate).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center justify-center px-3 py-1 text-xs font-medium rounded-lg w-fit ${
                      case_.status === 'APPROVED'
                        ? 'bg-green-100 text-green-800'
                        : case_.status === 'FOR_APPROVAL'
                        ? 'bg-purple-100 text-purple-800'
                        : case_.status === 'IN_RESEARCH'
                        ? 'bg-yellow-100 text-yellow-800'
                        : case_.status === 'FOR_VALIDATION'
                        ? 'bg-blue-100 text-blue-800'
                        : case_.status === 'REJECTED'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {case_.status === 'IN_RESEARCH' ? 'In research' : case_.status === 'FOR_APPROVAL' ? 'For approval' : case_.status === 'FOR_VALIDATION' ? 'For validation' : case_.status.charAt(0) + case_.status.slice(1).toLowerCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center justify-center px-3 py-1 text-xs font-medium rounded-lg w-fit ${
                      case_.priority >= 5 ? 'bg-red-100 text-red-800' :
                      case_.priority >= 4 ? 'bg-orange-100 text-orange-800' :
                      case_.priority >= 3 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {getPriorityLabel(case_.priority)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {case_.documentorName || <span className="text-gray-400 italic">Unknown</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {case_.assignedTo ? (
                      <div className="flex items-center space-x-2">
                        <UserAvatar user={case_.assignedTo} size="sm" />
                        <div>
                          <span className="font-medium text-gray-900 dark:text-gray-100 block">
                            {case_.assignedTo.firstName} {case_.assignedTo.lastName}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{case_.assignedTo.role}</span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400 italic">Unassigned</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex space-x-2">
                      <Link
                        to={`/cases/${case_.id}`}
                        className="text-emerald-600 hover:text-emerald-900"
                      >
                        View
                      </Link>
                      <Link
                        to={`/cases/${case_.id}/edit`}
                        className="text-emerald-600 hover:text-emerald-900"
                      >
                        Edit
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {pagination.total > 0 ? (
              <>
                Showing{' '}
                <span className="font-medium">
                  {Math.min((currentPage - 1) * pagination.limit + 1, pagination.total)}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * pagination.limit, pagination.total)}
                </span>{' '}
                of{' '}
                <span className="font-medium">{pagination.total}</span> results
              </>
            ) : (
              'No results'
            )}
          </p>
          <select
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
            value={pageSize}
            onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>
        {pagination.totalPages > 1 && (
          <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 text-sm font-medium ${
                    currentPage === 1
                      ? 'text-gray-300 cursor-not-allowed bg-gray-50 dark:bg-gray-900'
                      : 'text-gray-500 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => {
                  if (
                    page === 1 ||
                    page === pagination.totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-emerald-50 dark:bg-emerald-900/30 border-emerald-500 text-emerald-600'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 2 ||
                    page === currentPage + 2
                  ) {
                    return (
                      <span
                        key={page}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pagination.totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 text-sm font-medium ${
                    currentPage === pagination.totalPages
                      ? 'text-gray-300 cursor-not-allowed bg-gray-50 dark:bg-gray-900'
                      : 'text-gray-500 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="sr-only">Next</span>
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </nav>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default Cases;